import { FC, useEffect, useState } from 'react';
import { playSound } from './play-sound';
import inactiveButton from '../assets/sound-effects/daily-bounties/inactive-button-02.mp3';
import activeButton from '../assets/sound-effects/daily-bounties/active-button-01.mp3';
import { ConfirmPopUp } from '../components/ConfirmPopup';

const SOUNDS_MAP = {
  CONFIRM: activeButton,
  CANCEL: inactiveButton,
};

type ConfirmArgs = { message: string; title: string };
type UseConfirmPopUpOptions = { confirmOnly?: boolean; withSound?: boolean };
type UseConfirmPopUpReturn = [FC, (a: ConfirmArgs) => Promise<boolean>];

export const useConfirmPopUp = (
  { confirmOnly = false, withSound = false }: UseConfirmPopUpOptions = {},
): UseConfirmPopUpReturn => {
  const [promise, setPromise] = useState<{ resolve:(v: boolean) => void } | null>(null);
  const [message, setErrorMessage] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const confirm = ({ message: msg, title: t }: ConfirmArgs) => new Promise<boolean>((resolve) => {
    setShowPopup(true);
    setErrorMessage(msg);
    setTitle(t);
    setPromise({ resolve });
  });

  const handleClose = () => {
    setPromise(null);
    setShowPopup(false);
    setErrorMessage('');
    setTitle('');
  };

  const handleConfirm = () => {
    if (withSound) {
      playSound(SOUNDS_MAP.CONFIRM);
    }
    promise?.resolve(true);
    handleClose();
  };

  const handleCancel = () => {
    if (withSound) {
      playSound(SOUNDS_MAP.CANCEL);
    }
    promise?.resolve(false);
    handleClose();
  };

  useEffect(() => {
    document.body.style.overflow = showPopup ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [showPopup]);

  function ConfirmationDialog() {
    if (!showPopup) return null;

    return (
      <ConfirmPopUp
        title={title}
        message={message}
        confirmOnly={confirmOnly}
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
        handleClose={handleClose}
      />
    );
  }

  return [ConfirmationDialog, confirm];
};
